import type { JournalBeleg } from '../api';
import { istStornonummerVon } from './berechnung';

/**
 * Originalbeleg finden (Retoure Stufe 1): Eingabe deuten und vorhandene Daten auswerten.
 *
 * - QR-Code: Ein Handscanner (HID) tippt den RKSV-Datenblock `_R1-AT1_<Kassen-ID>_<Belegnummer>_…`
 *   ins fokussierte Suchfeld und schließt mit Enter ab. Kassen-ID und Belegnummer stehen an
 *   Stelle 2 und 3 (wie `zerlegeDatenblock` im Kern). Die Kamera liest heute nur EAN
 *   (`kamera-scan.ts`); QR per Kamera ist eine spätere Erweiterung.
 * - Belegnummer: exakter Abruf an dieser Kasse.
 * - Betrag („54,96“) oder Text: Belegjournal dieser Kasse (Suche nach Betrag nur in den
 *   zuletzt geladenen Belegen; serverseitige Betragssuche und kassenübergreifende Suche = RU-2).
 */

export type SuchEingabe =
  | { art: 'leer' }
  | { art: 'qr'; kassenId: string; belegnummer: string }
  | { art: 'qr-ungueltig' }
  | { art: 'nummer'; belegnummer: string }
  | { art: 'betrag'; cent: number }
  | { art: 'text'; text: string };

const BETRAG = /^-?\d{1,7},\d{2}\s*€?$/;

export function deuteEingabe(roh: string): SuchEingabe {
  const text = roh.trim();
  if (text === '') {
    return { art: 'leer' };
  }
  // Bon-QR: Datenblock ab „_R1-“ (ein Scannerpräfix oder Steuerzeichen davor wird übergangen)
  const qrStart = text.indexOf('_R1-');
  if (qrStart >= 0 && qrStart <= 4) {
    const t = text.slice(qrStart).split('_');
    const kassenId = t[2] ?? '';
    const belegnummer = t[3] ?? '';
    // Datenblock: 12 Felder mit führendem „_“, im QR-Code zusätzlich die Signatur
    if (t.length < 13 || !/^[\w.-]{1,64}$/.test(kassenId) || !/^[\w.-]{1,64}$/.test(belegnummer)) {
      return { art: 'qr-ungueltig' };
    }
    return { art: 'qr', kassenId, belegnummer };
  }
  if (BETRAG.test(text)) {
    const [euro = '0', cent = '00'] = text.replace(/[€\s-]/g, '').split(',');
    const betrag = Number(euro) * 100 + Number(cent);
    return { art: 'betrag', cent: text.startsWith('-') ? -betrag : betrag };
  }
  // Belegnummern: ein Wort mit Ziffer oder Bindestrich (B-20261018-XRHU-0042, ST-…, R-…)
  if (/^[\w.-]+$/.test(text) && /[\d-]/.test(text)) {
    return { art: 'nummer', belegnummer: text };
  }
  return { art: 'text', text };
}

/** Kommt der Treffer als Original in Frage (Verkaufsbeleg, kein Sonderbeleg)? */
export function istVerkaufsbeleg(b: Pick<JournalBeleg, 'belegtyp' | 'sonderbeleg'>): boolean {
  return b.belegtyp === 'STANDARD' && b.sonderbeleg === null;
}

/**
 * Stornobeleg zum Original aus Journaltreffern: Nummer `ST-<Original>[-Ziffern]`, Typ STORNO,
 * nicht vor dem Original. Das Journal liefert keine Beleg-ID; der Zeitpunkt ersetzt die
 * ID-Reihenfolge des Servers (ein Storno entsteht immer nach seinem Original).
 */
export function stornoAusJournal(
  original: { belegnummer: string; zeitpunkt: string },
  treffer: readonly Pick<JournalBeleg, 'belegnummer' | 'belegtyp' | 'zeitpunkt'>[],
): string | null {
  const ab = Date.parse(original.zeitpunkt);
  return (
    treffer.find(
      (b) =>
        b.belegtyp === 'STORNO' &&
        istStornonummerVon(original.belegnummer, b.belegnummer) &&
        Date.parse(b.zeitpunkt) >= ab,
    )?.belegnummer ?? null
  );
}
